import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Alert from '@mui/material/Alert'
import TextField from '@mui/material/TextField'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import RefreshIcon from '@mui/icons-material/Refresh'
import VerifiedIcon from '@mui/icons-material/Verified'
import GppMaybeIcon from '@mui/icons-material/GppMaybe'
import { exportAudit, getRd, type AuditEvent, type ResearchDomain } from '../services/authApi'

export function AuditLogPage() {
  const { rdId } = useParams<{ rdId: string }>()
  const navigate = useNavigate()
  const [rd, setRd] = useState<ResearchDomain | null>(null)
  const [events, setEvents] = useState<AuditEvent[]>([])
  const [chainIntact, setChainIntact] = useState(true)
  const [batches, setBatches] = useState(0)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    if (!rdId) return
    setLoading(true)
    setError('')
    try {
      const [rdData, audit] = await Promise.all([
        getRd(rdId),
        exportAudit(rdId, from || undefined, to || undefined),
      ])
      setRd(rdData.rd)
      setEvents(audit.events.sort((a, b) => b.seq - a.seq))
      setChainIntact(audit.chainIntact)
      setBatches(audit.batchesScanned)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit log')
    } finally {
      setLoading(false)
    }
  }, [rdId, from, to])

  useEffect(() => { load() }, [rdId]) // eslint-disable-line react-hooks/exhaustive-deps

  function downloadNdjson() {
    const blob = new Blob([events.map(e => JSON.stringify(e)).join('\n')], { type: 'application/x-ndjson' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `audit-${rdId}-${new Date().toISOString().slice(0, 10)}.ndjson`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (!rdId) return null

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate(`/rds/${rdId}`)} size="small"><ArrowBackIcon fontSize="small" /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h3">Audit Log</Typography>
          <Typography variant="body2" color="text.secondary">{rd?.name} — immutable trail (R2 WORM, hash-chained)</Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
        <Button variant="contained" onClick={downloadNdjson} disabled={!events.length}>Export NDJSON</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
        <TextField size="small" label="From (ISO)" placeholder="2026-06-01" value={from} onChange={e => setFrom(e.target.value)} />
        <TextField size="small" label="To (ISO)" placeholder="2026-06-30" value={to} onChange={e => setTo(e.target.value)} />
        <Button size="small" onClick={load} disabled={loading}>Apply</Button>
        <Box sx={{ flex: 1 }} />
        {chainIntact ? (
          <Chip icon={<VerifiedIcon />} label={`Hash chain intact — ${batches} batches`} color="success" variant="outlined" />
        ) : (
          <Chip icon={<GppMaybeIcon />} label="Hash chain BROKEN — tampering detected" color="error" />
        )}
      </Box>

      {events.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider' }} elevation={0}>
          <Typography color="text.secondary">{loading ? 'Loading…' : 'No audit events for this range.'}</Typography>
        </Paper>
      ) : (
        <Paper sx={{ border: '1px solid', borderColor: 'divider', overflow: 'auto' }} elevation={0}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& th, & td': { px: 1.5, py: 1, textAlign: 'left', fontSize: 13 } }}>
            <thead>
              <tr>
                {['Seq', 'Time', 'Actor', 'Type', 'Category', 'Resource', 'Action', 'Effect', 'Reason'].map(h => (
                  <Box component="th" key={h} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="overline">{h}</Typography>
                  </Box>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.map(e => (
                <Box component="tr" key={e.seq} sx={{ '&:hover': { bgcolor: 'action.hover' }, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <td>{e.seq}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{e.ts?.replace('T', ' ').slice(0, 19)}</td>
                  <td style={{ fontFamily: 'monospace' }}>{e.actorUserId}{e.isSystemAdmin ? ' ⚑' : ''}</td>
                  <td>{e.actorType}</td>
                  <td><Chip size="small" label={e.category} variant="outlined" color={e.category === 'mutation' ? 'secondary' : 'default'} /></td>
                  <td>{e.resourceType ?? '—'}</td>
                  <td>{e.action ?? '—'}</td>
                  <td>{e.effect ? <Chip size="small" label={e.effect} color={e.effect === 'allow' ? 'success' : 'error'} variant="outlined" /> : '—'}</td>
                  <td style={{ color: '#666', maxWidth: 280 }}>{e.reason}</td>
                </Box>
              ))}
            </tbody>
          </Box>
        </Paper>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        ⚑ = system-admin (support) access. The trail is written to R2 with Object Lock (WORM); the integrity badge verifies the
        per-batch hash chain so any alteration is detectable.
      </Typography>
    </Box>
  )
}
